import csrfToken from './csrf_token';

// Debounced PATCH of a single field value — the Hotwire counterpart of
// frontend/components/styleguide/hooks/useAutoSave.ts. Shared by the UI Kit
// field controllers so each one only owns *how* it renders the error, not the
// request plumbing (debounce, abort, CSRF, error extraction).
//
// Usage from a controller:
//
//   import AutoSave from 'stimulus/auto_save';
//
//   connect() {
//     this.autoSave = new AutoSave({
//       url: this.autoSaveUrlValue,
//       name: this.fieldTarget.name,
//       debounce: this.debounceValue,
//       label: this.identifier,
//       onSaved: value => this.dispatch('saved', { detail: { value } }),
//       onError: message => this.showError(message)
//     });
//   }
//
//   disconnect() { this.autoSave.cancel(); }
//   handleInput() { this.autoSave.save(this.fieldTarget.value); }
//
// Every successful save also emits a bubbling `autosave:saved` on `element` (the field's
// root, or `document` when none is given), carrying `{ name, value }`. It is the one
// signal that fires for every autosaving field regardless of who owns the save, which is
// what the job post's preview drawer listens on to refresh itself.
//
// `save` accepts a string, an array of strings (multi-select, checkbox groups)
// or a File (uploads), matching the hook's buildFormData. Composite fields that
// submit several inputs at once (location-field-component) pass a ready-made
// FormData instead, and give `name` the list of keys their errors can arrive
// under.
const buildFormData = (name, value) => {
  if (value instanceof FormData) return value;

  const formData = new FormData();

  if (Array.isArray(value)) {
    value.forEach(entry => formData.append(name, entry));
  } else {
    formData.append(name, value);
  }

  return formData;
};

// `talent[headline]` -> `headline`, the key Rails uses in its `errors` payload.
// A trailing `[]` (array fields) is dropped first, so `job_ad[skill_ids][]` also resolves.
const fieldKey = name => (name || '').replace(/\[\]$/, '').replace(/.*\[(\w+)\]$/, '$1');

export default class AutoSave {
  constructor({ url, name, debounce = 500, onSaved, onError, element, label = 'auto-save' }) {
    this.url = url;
    this.name = name;
    this.debounce = debounce;
    this.onSaved = onSaved;
    this.onError = onError;
    this.element = element;
    this.label = label;
  }

  // Debounces: only the last value within the window is sent.
  save(value) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.perform(value), this.debounce);
  }

  // For once-per-edit triggers (`change`, blur): there is nothing to coalesce, and a
  // debounced save is cancelled by the disconnect that follows navigating away.
  saveNow(value) {
    clearTimeout(this.timer);
    this.perform(value);
  }

  // Drops a pending save and aborts one in flight — call from `disconnect()`.
  cancel() {
    clearTimeout(this.timer);
    this.abortController?.abort();
  }

  async perform(value) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(this.url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken(),
          Accept: 'application/json'
        },
        body: buildFormData(this.name, value),
        signal: this.abortController.signal
      });

      if (response.ok) {
        this.onSaved?.(value);
        this.announceSaved(value);
        return;
      }

      const data = await response.json().catch(() => ({}));
      const message = this.errorFor(data.errors);
      if (message) this.onError?.(message);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn(`[${this.label}] auto-save failed`, error); // eslint-disable-line no-console
    }
  }

  announceSaved(value) {
    (this.element ?? document).dispatchEvent(new CustomEvent('autosave:saved', {
      bubbles: true,
      composed: true,
      detail: { name: this.name, value }
    }));
  }

  // First error among the field's keys, so a composite field can report on
  // whichever of its inputs the server rejected.
  errorFor(errors) {
    if (!errors) return null;

    const names = Array.isArray(this.name) ? this.name : [this.name];
    const message = names.map(name => errors[fieldKey(name)]).find(Boolean);

    return Array.isArray(message) ? message[0] : message;
  }
}
